import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";

// دالة مساعدة للحصول على التوكن
const getAuthToken = () => {
  try {
    // البحث في sessionStorage أولاً
    let storedUser = sessionStorage.getItem("loggedUser");
    if (!storedUser) {
      // إذا لم يوجد في sessionStorage، ابحث في localStorage
      storedUser = localStorage.getItem("loggedUser");
    }

    if (!storedUser) {
      console.error("❌ No user data found in sessionStorage or localStorage");
      return null;
    }

    const parsedUser = JSON.parse(storedUser);
    let token = parsedUser?.token;
    if (!token) {
      token = parsedUser?.data?.token;
    }
    if (!token) {
      token = parsedUser?.user?.token;
    }
    if (!token) {
      token = parsedUser?.result?.token;
    }

    if (!token) {
      console.error("❌ No token found in user data");
      return null;
    }

    // console.log("✅ Token found:", token);
    return token;
  } catch (error) {
    console.error("❌ Error getting token:", error);
    return null;
  }
};

// Fetch FAQs
export const fetchFAQs = createAsyncThunk(
  "faqs/fetchFAQs",
  async (page = 1, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(
        `${BASE_URL}/dashboard/faqs?page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": "ar",
          },
        }
      );

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "فشل في تحميل الأسئلة الشائعة";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Add FAQ
export const addFAQ = createAsyncThunk(
  "faqs/addFAQ",
  async (faqData, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      // console.log('FAQ Data being sent:', faqData);
      // console.log('API URL:', `${BASE_URL}/dashboard/faqs`);

      const response = await axios.post(
        `${BASE_URL}/dashboard/faqs`,
        {
          q_en: faqData.q_en,
          q_ar: faqData.q_ar,
          a_en: faqData.a_en,
          a_ar: faqData.a_ar,
          status: faqData.status,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": "ar",
          },
        }
      );

      // console.log('Add FAQ Response:', response.data);

      return {
        message: response.data?.message || "FAQ added successfully",
        data: response.data?.data || response.data,
        success: true,
      };
    } catch (error) {
      const message =
        error.response?.data?.message || "فشل في إضافة السؤال الشائع";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update FAQ
export const updateFAQ = createAsyncThunk(
  "faqs/updateFAQ",
  async ({ id, faqData }, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/dashboard/faqs/${id}/update`,
        {
          q_en: faqData.q_en,
          q_ar: faqData.q_ar,
          a_en: faqData.a_en,
          a_ar: faqData.a_ar,
          status: faqData.status,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": "ar",
          },
        }
      );

      return {
        message: response.data?.message || "FAQ updated successfully",
        data: response.data?.data || response.data,
        success: true,
      };
    } catch (error) {
      const message =
        error.response?.data?.message || "فشل في تحديث السؤال الشائع";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Delete FAQ
export const deleteFAQ = createAsyncThunk(
  "faqs/deleteFAQ",
  async (id, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/dashboard/faqs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": "ar",
        },
      });

      return {
        message: response.data?.message || "FAQ deleted successfully",
        id: id,
        success: true,
      };
    } catch (error) {
      const message =
        error.response?.data?.message || "فشل في حذف السؤال الشائع";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const initialState = {
  data: [],
  meta: null,
  loading: false,
  error: null,
  addLoading: false,
  addError: null,
  addSuccessMessage: null,
  updateLoading: false,
  updateError: null,
  updateSuccessMessage: null,
  deleteLoading: false,
  deleteError: null,
  deleteSuccessMessage: null,
};

const faqSlice = createSlice({
  name: "faqs",
  initialState,
  reducers: {
    clearFAQState: (state) => {
      state.error = null;
      state.addError = null;
      state.addSuccessMessage = null;
      state.updateError = null;
      state.updateSuccessMessage = null;
      state.deleteError = null;
      state.deleteSuccessMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch FAQs
      .addCase(fetchFAQs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFAQs.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data || action.payload;
        state.meta = action.payload.meta || null;
        state.error = null;
      })
      .addCase(fetchFAQs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add FAQ
      .addCase(addFAQ.pending, (state) => {
        state.addLoading = true;
        state.addError = null;
        state.addSuccessMessage = null;
      })
      .addCase(addFAQ.fulfilled, (state, action) => {
        state.addLoading = false;
        state.addSuccessMessage = action.payload.message;
        state.addError = null;
        // لا نحتاج لإضافة الـ FAQ يدوياً لأننا سنعيد تحميل البيانات
      })
      .addCase(addFAQ.rejected, (state, action) => {
        state.addLoading = false;
        state.addError = action.payload;
        state.addSuccessMessage = null;
      })

      // Update FAQ
      .addCase(updateFAQ.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccessMessage = null;
      })
      .addCase(updateFAQ.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccessMessage = action.payload.message;
        state.updateError = null;
        // تحديث الـ FAQ في القائمة
        if (action.payload.data) {
          const index = state.data.findIndex(
            (faq) => faq.id === action.payload.data.id
          );
          if (index !== -1) {
            state.data[index] = action.payload.data;
          }
        }
      })
      .addCase(updateFAQ.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
        state.updateSuccessMessage = null;
      })

      // Delete FAQ
      .addCase(deleteFAQ.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccessMessage = null;
      })
      .addCase(deleteFAQ.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccessMessage = action.payload.message;
        state.deleteError = null;
        // حذف الـ FAQ من القائمة
        state.data = state.data.filter((faq) => faq.id !== action.payload.id);
      })
      .addCase(deleteFAQ.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
        state.deleteSuccessMessage = null;
      });
  },
});

export const { clearFAQState } = faqSlice.actions;
export default faqSlice.reducer;
